import { useState, type FormEvent } from 'react';
import { Ticket } from 'lucide-react';
import { Button, Input, TextButton, Typography } from '../../symphony/ui';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { CREDITS_REFRESH_EVENT } from '../../../lib/use-credits';
import { normalizePromoCode } from '../../../lib/promo-code';
import { redeemPromo } from '../../../lib/redeem-promo';
import { promoFormMessage, type PromoFormMessage } from '../../../lib/promo-form';
import { SETTINGS_PAGE } from './settings-shared';
import styles from './RedeemCodeEntry.module.css';

/**
 * "Redeem a code" on the plan card: the typed twin of the `/promo/<code>` campaign link. It spends
 * the code through the same `/api/promo-redeem` route, so one redemption rule serves both.
 */
export function RedeemCodeEntry() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<PromoFormMessage | null>(null);

  if (!open) {
    return (
      <TextButton
        size="small"
        underline
        startIcon={<Ticket size={16} aria-hidden />}
        className={styles.entry}
        onClick={() => {
          logPageClickBi({ ...SETTINGS_PAGE, button_name: 'redeem_code_open' });
          setOpen(true);
        }}
      >
        {t('settings.promo.entry')}
      </TextButton>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizePromoCode(code);
    logPageClickBi({ ...SETTINGS_PAGE, button_name: 'redeem_code_submit' });
    if (!normalized) {
      setMessage(promoFormMessage('unknown', 0, 0));
      return;
    }
    setBusy(true);
    const result = await redeemPromo(normalized, '');
    setBusy(false);
    const next = promoFormMessage(result.outcome, result.credits, result.freeHours);
    setMessage(next);
    if (next.clearInput) setCode('');
    if (result.outcome === 'redeemed' && typeof window !== 'undefined') window.dispatchEvent(new Event(CREDITS_REFRESH_EVENT));
  };

  const values = message?.values
    ? Object.fromEntries(Object.entries(message.values).map(([k, v]) => [k, v.toLocaleString()]))
    : undefined;

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.row}>
        <Input
          aria-label={t('settings.promo.label')}
          placeholder={t('settings.promo.placeholder')}
          value={code}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          onChange={(e) => {
            setCode(e.target.value);
            if (message) setMessage(null);
          }}
          status={message?.tone === 'error' ? 'error' : 'default'}
          statusMessage={message?.tone === 'error' ? t(message.key, values) : undefined}
        />
        <Button type="submit" size="large" loading={busy} disabled={!code.trim()}>
          {t('settings.promo.redeem')}
        </Button>
      </div>
      {message && message.tone !== 'error' && (
        <Typography variant="p2" component="p" role="status" color={message.tone === 'success' ? 'primary' : 'secondary'}>
          {t(message.key, values)}
        </Typography>
      )}
    </form>
  );
}
